import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import AdminLayout from "../components/AdminLayout";
import { useAuth } from "../context/AuthContext";
import { useSiteContent } from "../context/SiteContentContext";
import { Save, Upload } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sections defined in groups. Each field is { key, label, type ('text'|'textarea'|'image'|'single') }
// type 'single' => single value, no SQ/EN
const SECTIONS = [
  {
    id: "home",
    label: "Kryefaqja",
    fields: [
      { key: "home.hero.overline", label: "Overline", type: "text" },
      { key: "home.hero.title", label: "Titulli", type: "text" },
      { key: "home.hero.subtitle", label: "Nëntitulli", type: "textarea" },
      { key: "home.hero.cta", label: "Butoni kryesor", type: "text" },
      { key: "home.hero.learn_more", label: "Butoni dytësor", type: "text" },
      { key: "home.hero.background", label: "Foto e sfondit", type: "image" },
    ],
  },
  {
    id: "about",
    label: "Rreth Nesh",
    fields: [
      { key: "about.title", label: "Titulli", type: "text" },
      { key: "about.subtitle", label: "Nëntitulli", type: "text" },
      { key: "about.history.title", label: "Origjina - Titulli", type: "text" },
      { key: "about.history.content", label: "Origjina - Përmbajtja", type: "textarea" },
      { key: "about.history.image", label: "Origjina - Foto", type: "image" },
      { key: "about.philosophy.title", label: "Filozofia - Titulli", type: "text" },
      { key: "about.philosophy.content", label: "Filozofia - Përmbajtja", type: "textarea" },
      { key: "about.philosophy.image", label: "Filozofia - Foto", type: "image" },
      { key: "about.mission.title", label: "Misioni - Titulli", type: "text" },
      { key: "about.mission.content", label: "Misioni - Përmbajtja", type: "textarea" },
    ],
  },
  {
    id: "sustainability",
    label: "Qëndrueshmëria",
    fields: [
      { key: "sustainability.overline", label: "Overline", type: "text" },
      { key: "sustainability.title", label: "Titulli", type: "text" },
      { key: "sustainability.subtitle", label: "Nëntitulli", type: "textarea" },
      { key: "sustainability.eu_desc", label: "Përshkrimi BE", type: "textarea" },
      { key: "sustainability.future_title", label: "Titulli i seksionit të dytë", type: "text" },
      { key: "sustainability.future_desc", label: "Përshkrimi i të ardhmes", type: "textarea" },
    ],
  },
  {
    id: "contact",
    label: "Kontakti",
    fields: [
      { key: "contact.email", label: "Email", type: "single" },
      { key: "contact.phone", label: "Telefon", type: "single" },
      { key: "contact.slaughterhouse_address", label: "Adresa Thertores", type: "text" },
      { key: "contact.slaughterhouse_hours", label: "Orari Thertores", type: "text" },
      { key: "contact.salami_address", label: "Adresa Sallamerise", type: "text" },
      { key: "contact.salami_hours", label: "Orari Sallamerise", type: "text" },
      { key: "contact.maps_url", label: "Google Maps URL", type: "single" },
      { key: "contact.social.facebook", label: "Facebook URL", type: "single" },
      { key: "contact.social.instagram", label: "Instagram URL", type: "single" },
      { key: "contact.social.linkedin", label: "LinkedIn URL", type: "single" },
    ],
  },
];

const ImageUploader = ({ url, onChange, authHeaders }) => {
  const [uploading, setUploading] = useState(false);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Foto u ngarkua");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gabim në ngarkim");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-start gap-3">
      {url ? (
        <div className="w-32 h-20 bg-zinc-100 overflow-hidden border border-zinc-200 flex-shrink-0">
          <img src={url} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="w-32 h-20 bg-zinc-100 border border-dashed border-zinc-300 flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <input type="text" value={url || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-zinc-300 px-3 py-2 text-sm" placeholder="URL e fotos" />
        <label className="mt-2 inline-flex items-center gap-2 text-xs cursor-pointer text-[#0A0A0A] hover:underline">
          <Upload className="w-3 h-3" />
          {uploading ? "Duke ngarkuar..." : "Ngarko foto të re"}
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onPick} disabled={uploading} className="hidden" />
        </label>
      </div>
    </div>
  );
};

const AdminSiteContent = () => {
  const { authHeaders } = useAuth();
  const { refresh: refreshSiteContent } = useSiteContent();
  const [activeSection, setActiveSection] = useState("home");
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/site-settings`);
      setSettings(data);
    } catch {
      toast.error("Gabim");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateField = (key, lang, value) => {
    setSettings((s) => {
      const current = s[key] || {};
      return { ...s, [key]: { ...current, [lang]: value } };
    });
  };
  const updateSingle = (key, value) => {
    setSettings((s) => ({ ...s, [key]: { value } }));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const section = SECTIONS.find((s) => s.id === activeSection);
      const updates = section.fields.map((f) => ({ key: f.key, value: settings[f.key] || {} }));
      await axios.put(`${API}/admin/site-settings`, updates, { headers: authHeaders() });
      // Push the change to the global context so every public page sees it immediately
      await refreshSiteContent();
      toast.success("Përmbajtja u ruajt — ndryshimet janë të dukshme në faqe");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gabim në ruajtje");
    } finally {
      setSaving(false);
    }
  };

  const activeFields = SECTIONS.find((s) => s.id === activeSection)?.fields || [];

  return (
    <AdminLayout>
      <div data-testid="admin-site-content-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Përmbajtja e Faqes</h1>
            <p className="text-zinc-600 mt-1">Menaxho tekstet dhe fotot e seksioneve të website-it</p>
          </div>
          <button data-testid="content-save-button" onClick={onSave} disabled={saving} className="bg-[#0A0A0A] text-white px-6 py-3 font-medium hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-2">
            <Save className="w-4 h-4" /> {saving ? "Duke ruajtur..." : "Ruaj Ndryshimet"}
          </button>
        </div>

        {/* Section tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-zinc-200">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              data-testid={`content-tab-${s.id}`}
              onClick={() => setActiveSection(s.id)}
              className={`px-4 py-3 font-medium text-sm border-b-2 -mb-px transition-colors ${
                activeSection === s.id ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-zinc-500 hover:text-[#0A0A0A]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-zinc-500">Duke ngarkuar...</div>
        ) : (
          <div className="bg-white border border-zinc-200">
            {activeFields.map((field) => {
              const v = settings[field.key] || {};
              return (
                <div key={field.key} className="border-b border-zinc-100 p-6 last:border-b-0">
                  <p className="text-sm font-semibold text-[#0A0A0A] mb-3">{field.label}</p>
                  {field.type === "image" ? (
                    <ImageUploader url={v.value || ""} onChange={(val) => updateSingle(field.key, val)} authHeaders={authHeaders} />
                  ) : field.type === "single" ? (
                    <input type="text" value={v.value || ""} onChange={(e) => updateSingle(field.key, e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none" />
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-zinc-500 mb-1">Shqip</label>
                        {field.type === "textarea" ? (
                          <textarea rows={4} value={v.sq || ""} onChange={(e) => updateField(field.key, "sq", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none resize-none" />
                        ) : (
                          <input type="text" value={v.sq || ""} onChange={(e) => updateField(field.key, "sq", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none" />
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-zinc-500 mb-1">English</label>
                        {field.type === "textarea" ? (
                          <textarea rows={4} value={v.en || ""} onChange={(e) => updateField(field.key, "en", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none resize-none" />
                        ) : (
                          <input type="text" value={v.en || ""} onChange={(e) => updateField(field.key, "en", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none" />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSiteContent;
